import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Modal,
  message,
  Spin,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import { InputSearchPro } from 'components/InputSearch';
import { getCanalesDePago, patchCanalDePago, postCanalDePago } from 'api/configuracion/canalDePago';
import { getCatalogoDeCanalesDePago, getTiposDePagos } from 'api/configuracion';
import { getUsuarios, patchUsuario } from 'api/usuarios/usuarios';

const ModalCanalDePago = ({
  visible,
  setVisible,
  form,
  setUsersFound,
  usersFound,
  mutable,
}) => {
  const [loading, setLoading] = useState(false);
  const [tiposDePago, setTiposDePago] = useState([]);
  const [catalogosDeCanalesDePago, setCatalogosDeCanalesDePago] = useState([]);
  const [canales, setCanales] = useState([]);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const [
        _catalogoDeCanalesDePago,
        _tiposDePago,
        _canalesDePago,
      ] = await Promise.all([
        getCatalogoDeCanalesDePago(),
        getTiposDePagos(),
        getCanalesDePago(),
      ]);
      if (mounted) {
        setCatalogosDeCanalesDePago(_catalogoDeCanalesDePago.filter((e) => e.id !== 2) || []);
        setTiposDePago(_tiposDePago);
        setCanales(_canalesDePago);
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const _canalesDePago = await getCanalesDePago();
    setCanales(_canalesDePago);
    setLoading(false);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const match = canales.find((e) => e.clave === values.clave);
      if ('q' in values) {
        delete values.q;
      }
      const response = await (match ? patchCanalDePago(match.id, values) : postCanalDePago(values));
      if (response) {
        await patchUsuario(response.id, { canal_de_pago: values.usuario });
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rules = {
    required: [{ required: true }],
  };

  const reset = () => {
    setUsersFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const busquedaUsuario = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      const _usuarios = await getUsuarios({ email: q });
      if (_usuarios.length) {
        const [usuario] = _usuarios;
        form.setFieldsValue({
          usuario: usuario.id,
        });
        setUsersFound([usuario]);
      } else {
        message.info('No se encontraron coincidencias');
        setUsersFound([]);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  return (
    <Modal
      visible={visible}
      title={(
        <FormSubmitControls
          label="Configurar"
          onFinish={onFinish}
          onCancel={onCancel}
          loading={loading}
          mutable={mutable}
        />
      )}
      onCancel={onCancel}
      footer={null}
      closable={false}
      forceRender
      maskClosable={!loading}
      keyboard={!loading}
    >
      <Spin spinning={loading} tip="Cargando...">
        <Form
          layout="vertical"
          form={form}
          onFinish={() => onFinish()}
          scrollToFirstError
          initialValues={{ estados_globales: 1 }}
        >
          <Row>
            <Col span={24}>
              <Form.Item
                name="clave"
                rules={rules.required}
                label="Clave"
                hasFeedback
              >
                <Input allowClear disabled={!mutable} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                rules={rules.required}
                name="catalogo_de_canal_de_pago"
                label="Catálogo de Canal de pago"
                hasFeedback
              >
                <Select dataSource={catalogosDeCanalesDePago} disabled={!mutable} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="descripcion"
                rules={rules.required}
                label="Descripción"
                hasFeedback
              >
                <Input allowClear disabled={!mutable} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="tipo_de_pago"
                label="Tipo de pago"
                hasFeedback
              >
                <Select
                  dataSource={tiposDePago}
                  keyLabelRender
                  labelProp="concepto"
                  disabled={!mutable}
                />
              </Form.Item>
            </Col>
            <InputSearchPro
              label="Usuario"
              tooltip="Búsqueda por Correo"
              name="q"
              rules={rules.required}
              onPressEnter={() => busquedaUsuario()}
              disabled={!mutable}
              breakPoints={{
                md: 24,
                lg: 24,
                xl: 24,
              }}
              inputProps={{
                onPressEnter: () => busquedaUsuario(),
                onChange: ({ target: { value } }) => {
                  if (!value) {
                    reset();
                  }
                },
              }}
              resultProps={{
                name: 'usuario',
                label: 'Usuario',
                rules: rules.required,
                dataSource: Array.isArray(usersFound) ? usersFound : [usersFound],
                labelProp: 'email',
                onClear: reset,
                breakPoints: {
                  md: 24,
                },
              }}
            />
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

ModalCanalDePago.propTypes = {
  setVisible: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  setUsersFound: PropTypes.func.isRequired,
  usersFound: PropTypes.oneOfType([
    PropTypes.shape(),
    PropTypes.arrayOf(PropTypes.shape()),
  ]).isRequired,
  form: PropTypes.shape().isRequired,
  mutable: PropTypes.bool.isRequired,
};

export default ModalCanalDePago;
